import { ref, computed, watch } from 'vue'

const STORAGE_KEY = 'theme'
const VALID_MODES = ['light', 'dark', 'system']

function getSystemPreferenceMedia() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  return window.matchMedia('(prefers-color-scheme: dark)')
}

function readStoredMode() {
  const stored = localStorage.getItem(STORAGE_KEY)
  return VALID_MODES.includes(stored) ? stored : 'system'
}

const media = getSystemPreferenceMedia()

// Singleton state — shared across all consumers
const mode = ref(readStoredMode())
const systemPrefersDark = ref(media ? media.matches : false)

const isDark = computed(() =>
  mode.value === 'system' ? systemPrefersDark.value : mode.value === 'dark',
)

function applyTheme(dark) {
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light')
}

// Apply immediately on first import
applyTheme(isDark.value)

watch(isDark, applyTheme)

watch(mode, (val) => {
  if (val === 'system') {
    localStorage.removeItem(STORAGE_KEY)
  } else {
    localStorage.setItem(STORAGE_KEY, val)
  }
})

// Re-read the media query directly rather than trusting only the 'change'
// event, which can be missed entirely if the OS theme flips while this tab
// is backgrounded/suspended — leaving the page stuck on a stale preference
// even though mode is still 'system'.
function resyncSystemPreference() {
  if (media) systemPrefersDark.value = media.matches
}

if (media) {
  const handleSystemChange = (e) => {
    systemPrefersDark.value = e.matches
  }
  if (media.addEventListener) {
    media.addEventListener('change', handleSystemChange)
  } else if (media.addListener) {
    // Safari < 14 fallback
    media.addListener(handleSystemChange)
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') resyncSystemPreference()
    })
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', resyncSystemPreference)
  }
}

export function useTheme() {
  const setTheme = (next) => {
    if (!VALID_MODES.includes(next)) return
    mode.value = next
    if (next === 'system') {
      // Re-check now too, so re-selecting an already-active "system" option
      // (a no-op for the mode ref) still self-heals a stale/missed reading.
      resyncSystemPreference()
      applyTheme(isDark.value)
    }
  }

  // Manual toggle always resolves to an explicit choice based on the
  // currently-rendered theme, whether that came from a stored value or system.
  const toggleTheme = () => {
    mode.value = isDark.value ? 'light' : 'dark'
  }

  return { isDark, mode, setTheme, toggleTheme }
}
